// PG PL/pgSQL procedural local-variable completion helpers (ROUND2-C1.2).
//
// Detects when the cursor is inside a PL/pgSQL procedural body — either a
// `DO $$ … $$` anonymous block or a `CREATE [OR REPLACE] FUNCTION /
// PROCEDURE … LANGUAGE plpgsql AS $$ … $$` body — and emits
// `CompletionCandidate[]` for the variable declarations visible at the cursor
// via the PL/pgSQL grammar's `parseProceduralBody` + `resolveScopePlPgSql`.
//
// This is a per-dialect-resolver-level dispatch: the orchestrator is
// unmodified; all wiring lives inside `pgsqlResolver.ts` (caller) and this
// helper module.
//
// Tier mapping:
//   - Declared locals (`DECLARE v_x INT`)   → tier 1 (current-statement scope)
//   - Synthesized implicit `found` boolean  → tier 1 (same; from scope resolver)
//
// Scope gaps (documented, not blocking — each deserves its own capability
// slice):
//   - FOR-loop iterator variables (e.g. `FOR r IN SELECT … LOOP`) are not
//     surfaced by `resolveScopePlPgSql` in the current slice. They are
//     emitted as `record-decl` entries only when their SELECT INTO assignment
//     target is inferred; bare loop iterators with no assignment are
//     out-of-scope.
//   - Cursor parameters are not surfaced.
//   - `%TYPE` / `%ROWTYPE` attribute references produce candidates whose
//     `detail` string shows the raw attribute text (e.g. `users%ROWTYPE`)
//     rather than the expanded field list. Full expansion ships with a
//     dedicated dotted-access slice.
//
// Bundle-not-loaded guard: `parseProceduralBody` uses a dynamic import of the
// vendored ANTLR-generated lexer + parser. If the bundle is not yet loaded,
// the dynamic import still resolves from the generated TS source on disk.
// We wrap the call in try/catch and degrade gracefully so the DML-side
// suggestions still flow.

import type { CompletionCandidate } from '@/core/engines';
import { loadCompletionModule } from '@/features/intellisense/telemetry/providerCost';
import { scanDocument } from '@/features/sql-grammar/document/scanner';
import { findEnclosingPlpgsqlBlock } from './pgsqlExtras';
import { extractPgRoutineParams } from './pgsqlProceduralParams';
import { scopedProceduralParse } from './scopedProceduralParse';

// ─── Candidate emission ───────────────────────────────────────────────────────

/**
 * Emit `CompletionCandidate[]` for the variable declarations visible at
 * `offset` inside a PL/pgSQL procedural body.
 *
 * Calls `parseProceduralBody` (async, ANTLR-backed) from the
 * `sql-grammar-plpgsql` grammar package, then `resolveScopePlPgSql` to obtain
 * the in-scope variable names. Wrapped in try/catch so a missing bundle (before
 * the ANTLR parser has been warmed) produces an empty list without aborting the
 * full candidate build.
 */
export async function emitPgsqlProceduralLocalCandidates(
  source: string,
  offset: number,
  out: CompletionCandidate[],
): Promise<void> {
  // Routine signature parameters (CB4) — `CREATE FUNCTION foo(p_a int, …)`
  // parameters live in the signature OUTSIDE the `$$ … $$` body, so they are
  // not in `scope.variables` (DECLARE-block only). This path is text-based and
  // cheap, keeps FULL-source coordinates (`block.bodyStart`), and runs FIRST so
  // parameters still surface when the body parse is guarded out (oversize) or
  // the bundle isn't loaded yet. (`findEnclosingPlpgsqlBlock` is recomputed
  // here; threading the resolver's `plpgsqlBlock` in is deferred — the caller
  // lives outside this task's file scope.)
  const seen = new Set<string>();
  const block = findEnclosingPlpgsqlBlock(source, offset);
  if (block) {
    for (const p of extractPgRoutineParams(source, block.bodyStart)) {
      const key = p.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const typePart = p.typeText ? `${p.typeText} ` : '';
      out.push({
        label: p.name,
        insertText: p.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: `${typePart}(${p.mode} parameter)`,
        filterText: p.name,
      });
    }
  }

  // DECLARE-block locals via the statement/block-scoped parse (plan16 T4).
  // Parse ONLY the CREATE FUNCTION / DO statement enclosing the caret — the
  // dollar-quoted body shields it from `;`-splitting, so `scanDocument` yields
  // the whole statement — and cache it by slice text. Degrades to no locals
  // (params above still flow) on a not-yet-loaded bundle, a parse failure, or
  // an oversized slice.
  try {
    const { parseProceduralBody } = await loadCompletionModule(
      '@/features/sql-grammar-plpgsql/parse',
      () => import('@/features/sql-grammar-plpgsql/parse'),
    );
    const { resolveScopePlPgSql } = await loadCompletionModule(
      '@/features/sql-grammar-plpgsql/resolver/resolveScopePlPgSql',
      () => import('@/features/sql-grammar-plpgsql/resolver/resolveScopePlPgSql'),
    );

    const parseResult = await scopedProceduralParse({
      dialect: 'plpgsql',
      source,
      offset,
      sliceFor: pgsqlStatementSlice,
      parse: (sliceText) => parseProceduralBody(sliceText, { engine: 'postgres' }),
    });
    if (!parseResult) return; // oversize — parameters already emitted above.
    const parsed = parseResult.body;
    // Offset translation (T4 crux): the caret in slice-relative coordinates.
    const scopeOffset = offset - parseResult.sliceStart;
    const scope = resolveScopePlPgSql(parsed, scopeOffset);

    for (const v of scope.variables) {
      const key = v.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const detail = buildTypeDetail(v.typeRef);
      out.push({
        label: v.name,
        insertText: v.name,
        isSnippet: false,
        kind: 'variable',
        tier: 1,
        detail: `${detail} (local variable)`,
        filterText: v.name,
      });
    }
  } catch {
    // Bundle not yet loaded or parse failure — degrade gracefully.
    // The DML-side candidates (tables, columns, keywords) still flow.
  }
}

// ─── Slice derivation (plan16 T4) ────────────────────────────────────────────

/**
 * Return the `[start, end)` range of the PL/pgSQL statement (a `CREATE
 * FUNCTION`/`PROCEDURE … AS $$ … $$` or a `DO $$ … $$` block) that encloses
 * `offset`, or null (→ full source) when the caret is outside every scanned
 * statement. Dollar-quoting shields the body's inner `;` from splitting, so a
 * single `scanDocument` statement covers the whole routine. Exported for the
 * slice unit tests.
 */
export function pgsqlStatementSlice(
  source: string,
  offset: number,
): { start: number; end: number } | null {
  const scan = scanDocument(source, { engine: 'postgres' });
  const stmt = scan.statements.find(
    (s) => s.startOffset <= offset && offset <= s.endOffset,
  );
  return stmt ? { start: stmt.startOffset, end: stmt.endOffset } : null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

type TypeRef =
  | { kind: 'scalar'; text: string }
  | { kind: 'type-attr'; base: { name: string; schema?: string; database?: string }; attr: string }
  | { kind: 'row-type-attr'; base: { name: string; schema?: string; database?: string }; attr: string }
  | { kind: 'record' }
  | { kind: 'alias'; target: unknown };

function buildTypeDetail(typeRef: TypeRef): string {
  switch (typeRef.kind) {
    case 'scalar':
      return typeRef.text;
    case 'record':
      return 'RECORD';
    case 'alias':
      return 'ALIAS';
    case 'type-attr': {
      const base = typeRef.base.schema
        ? `${typeRef.base.schema}.${typeRef.base.name}`
        : typeRef.base.name;
      return `${base}${typeRef.attr}`;
    }
    case 'row-type-attr': {
      const base = typeRef.base.schema
        ? `${typeRef.base.schema}.${typeRef.base.name}`
        : typeRef.base.name;
      return `${base}${typeRef.attr}`;
    }
  }
}

